import { useMount, useUpdateEffect } from 'ahooks';
import React, { useLayoutEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../../../hooks/hooks';
import { useApiaComponentContext } from '../../apiaComponent/ApiaComponentContext';
import { useApiaFormContext } from '../../page/ApiaForm';
import ApiaDataGridRenderer from './ApiaDataGridRenderer';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import { IApiaDataGrid } from '../../../../../store/dataGridSlice/types';
import EditionForm from './EditionForm';
import { useRegisteredRenderDebug } from '../../../../../hooks/useRenderDebug';
import propsStore from '../../../../../store/propsStore/propsStore';
import DataGridIdentifier from './DataGridIdentifier';

const shouldDebug = false;

const ApiaDataGridComponent = () => {
  const {
    formData: { field, ...formData },
    ...formContext
  } = useApiaFormContext();
  const gridFieldContext = useApiaComponentContext<IApiaDataGrid>();
  const identifierContextValue = React.useMemo(
    () => ({
      gridId: gridFieldContext.fieldId,
      shouldDebug,
    }),
    [gridFieldContext.fieldId],
  );
  const { isEditionFormOpen } = useAppSelector((global) => {
    return {
      isEditionFormOpen:
        (global.dataGridSlice[gridFieldContext.fieldId]?.editionFormRow ??
          -1) >= 0,
    };
  }, shallowEqual);

  useMount(function initGrid() {
    store.dispatch(
      dataGridActions.init({
        id: gridFieldContext.fieldId,
        context: {
          grid: gridFieldContext,
          form: { ...formContext, formData },
        },
      }),
    );
  });
  const {
    currentPage,
    isLastPage,
    isUpdating,
    isStoreValueSet,
    shouldAlterOnlyLastPage,
  } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridFieldContext.fieldId];
    return {
      isStoreValueSet: state !== undefined,
      shouldAlterOnlyLastPage:
        gridFieldContext.field.properties.alterOnlyLastPage,
      isLastPage:
        gridFieldContext.field.curPage === gridFieldContext.field.pages &&
        gridFieldContext.field.pages !== undefined,
      currentPage: gridFieldContext.field.curPage,
      isUpdating: state?.updating,
    };
  }, shallowEqual);

  useRegisteredRenderDebug('dataGrid', {
    ...formContext,
    ...gridFieldContext,
    ...identifierContextValue,
    isEditionFormOpen,
    currentPage,
    isLastPage,
    isStoreValueSet,
    shouldAlterOnlyLastPage,
  });

  useUpdateEffect(
    function setEditableIfShouldAlterOnlyLastPage() {
      const cellIds = Object.keys(
        store.getState().dataGridSlice[gridFieldContext.fieldId]?.cells ?? {},
      );
      if (shouldAlterOnlyLastPage) {
        if (isLastPage) {
          cellIds.forEach((id) =>
            propsStore.updateField(id, { readOnly: false }),
          );
        }
      } else {
        cellIds.forEach((id) => propsStore.updateField(id, { readOnly: true }));
      }
    },
    [currentPage],
  );

  useUpdateEffect(() => {
    if (!isEditionFormOpen)
      void store.dispatch(
        dataGridActions.refresh({
          gridId: gridFieldContext.fieldId,
          fromEditionForm: true,
        }),
      );
  }, [isEditionFormOpen]);

  useLayoutEffect(() => {
    if (isUpdating)
      React.startTransition(() => {
        store.dispatch(
          dataGridActions.update({
            id: gridFieldContext.fieldId,
            updating: false,
          }),
        );
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdating]);

  if (!isStoreValueSet) return null;

  return (
    <DataGridIdentifier gridId={gridFieldContext.fieldId}>
      <ApiaDataGridRenderer />
      {isEditionFormOpen && <EditionForm />}
    </DataGridIdentifier>
  );
};

export default React.memo(ApiaDataGridComponent);
